import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './grapheditor.css';
import NavBar from '../../Components/NavBar/navbar';
import FooterSection from '../../Components/Footer/footer';
import MarkovGraphCanvas from '../../Components/Graph/MarkovGraphCanvas';
import ResourceMgmtPanel from '../../Components/ResourceMgmt/ResourceMgmtPanel';
import { API_ENDPOINTS } from '../../utils/config';
import { THEME, pageBackgroundSx, gridOverlaySx } from '../../theme';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RouteIcon from '@mui/icons-material/Route';

const PROBLEMS = [
    { value: 'analyze', label: 'Analyze chain' },
    { value: 'shortest_path', label: 'Shortest path' },
    { value: 'max_probability', label: 'Most likely path' },
    { value: 'value_iteration', label: 'Value iteration' },
];

function GraphEditor() {
    const [tabValue, setTabValue] = useState(0);
    const [graph, setGraph] = useState({ nodes: [], edges: [] });
    const [problem, setProblem] = useState('shortest_path');
    const [source, setSource] = useState('');
    const [target, setTarget] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [healthy, setHealthy] = useState(null);
    const [result, setResult] = useState(null);
    const [optimalEdgeIds, setOptimalEdgeIds] = useState([]);

    const nodeOptions = useMemo(
        () =>
            (graph.nodes || []).map((n) => ({
                id: n.id,
                label: n?.data?.label || n.id,
            })),
        [graph.nodes]
    );

    useEffect(() => {
        if (!source && nodeOptions[0]) setSource(nodeOptions[0].id);
        if (!target && nodeOptions[nodeOptions.length - 1]) {
            setTarget(nodeOptions[nodeOptions.length - 1].id);
        }
    }, [nodeOptions, source, target]);

    const checkHealth = useCallback(async () => {
        try {
            const resp = await fetch(`${API_ENDPOINTS.MARKOV_CHAINS}/health`);
            const data = await resp.json();
            setHealthy(resp.ok && data.status === 'healthy');
            return data;
        } catch {
            setHealthy(false);
            return null;
        }
    }, []);

    useEffect(() => {
        if (tabValue === 0) checkHealth();
    }, [checkHealth, tabValue]);

    const handleGraphChange = useCallback((next) => {
        setGraph(next);
    }, []);

    const handleTabChange = (_e, newValue) => {
        setTabValue(newValue);
        setError(null);
        setSuccess(null);
    };

    const runSolver = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        setResult(null);
        setOptimalEdgeIds([]);

        try {
            const health = await checkHealth();
            if (!health) {
                // Dev: start server_markov_solver.py if health check fails locally.
                setError("Server not responding. Try again later.");
                return;
            }

            const endpoint =
                problem === 'analyze'
                    ? `${API_ENDPOINTS.MARKOV_CHAINS}/analyze`
                    : `${API_ENDPOINTS.MARKOV_CHAINS}/optimize`;

            const body = {
                nodes: graph.nodes,
                edges: graph.edges,
                problem,
                source,
                target,
                gamma: 0.95,
            };

            const resp = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await resp.json();
            if (!resp.ok || data.success === false) {
                console.error('Markov solve failed:', data.error || data);
                setError('Something went wrong. Try again.');
                return;
            }

            setResult(data);
            setOptimalEdgeIds(data.optimal_edge_ids || []);
            if (problem === 'analyze') {
                setSuccess('Done.');
            } else if (problem === 'value_iteration') {
                setSuccess('Done.');
            } else {
                setSuccess(
                    `Path: ${(data.path_labels || data.path_nodes || []).join(' → ')}`
                );
            }
        } catch (err) {
            console.error(err);
            setError('Could not reach the Markov services.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ ...pageBackgroundSx, color: THEME.textMain }}>
            <Box sx={gridOverlaySx} />
            <NavBar />

            <Box sx={{ position: 'relative', zIndex: 1, pt: 12, px: { xs: 2, md: 4 }, pb: 2 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography
                        variant="overline"
                        sx={{ color: THEME.primary, fontWeight: 700, letterSpacing: 2 }}
                    >
                        EVOLVING SYSTEMS / ROBOTIC
                    </Typography>
                    <Typography variant="h3" fontWeight={800} color={THEME.textMain}>
                        Graph Editor
                    </Typography>
                    <Typography
                        variant="body1"
                        color={THEME.secondary}
                        sx={{ maxWidth: 720, mx: 'auto', mt: 1 }}
                    >
                        Build Markov transition graphs, or configure supply-chain models —
                        EOQ, transshipment, facility location, CVRP, and SDE inventory routing.
                    </Typography>
                    {tabValue === 0 && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Chip
                                size="small"
                                icon={<AccountTreeIcon />}
                                label={healthy ? 'Online' : 'Offline'}
                                sx={{
                                    bgcolor: healthy ? 'rgba(44,187,93,0.15)' : 'rgba(239,71,67,0.15)',
                                    color: healthy ? THEME.primary : THEME.accent,
                                    border: `1px solid ${healthy ? THEME.primary : THEME.accent}`,
                                }}
                            />
                            <Chip
                                size="small"
                                label={`${nodeOptions.length} nodes · ${(graph.edges || []).length} edges`}
                                sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, border: `1px solid ${THEME.border}` }}
                            />
                        </Box>
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        centered
                        textColor="inherit"
                        TabIndicatorProps={{ style: { backgroundColor: THEME.primary } }}
                    >
                        <Tab
                            label="Markov Chains"
                            icon={<AccountTreeIcon />}
                            iconPosition="start"
                            sx={{
                                color: THEME.secondary,
                                '&.Mui-selected': { color: THEME.primary },
                                textTransform: 'none',
                            }}
                        />
                        <Tab
                            label="Supply Chain"
                            icon={<LocalShippingIcon />}
                            iconPosition="start"
                            sx={{
                                color: THEME.secondary,
                                '&.Mui-selected': { color: THEME.primary },
                                textTransform: 'none',
                            }}
                        />
                    </Tabs>
                </Box>

                {tabValue === 1 && <ResourceMgmtPanel />}

                {tabValue === 0 && (
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={3}>
                                <Typography variant="caption" color={THEME.secondary}>
                                    Problem
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={problem}
                                        onChange={(e) => setProblem(e.target.value)}
                                        sx={{
                                            color: THEME.textMain,
                                            bgcolor: THEME.cardBg,
                                            '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.border },
                                        }}
                                    >
                                        {PROBLEMS.map((p) => (
                                            <MenuItem key={p.value} value={p.value}>
                                                {p.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {problem !== 'analyze' && problem !== 'value_iteration' && (
                                <>
                                    <Grid item xs={6} md={2}>
                                        <Typography variant="caption" color={THEME.secondary}>
                                            Source
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={source}
                                                onChange={(e) => setSource(e.target.value)}
                                                sx={{
                                                    color: THEME.textMain,
                                                    bgcolor: THEME.cardBg,
                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.border },
                                                }}
                                            >
                                                {nodeOptions.map((n) => (
                                                    <MenuItem key={n.id} value={n.id}>
                                                        {n.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} md={2}>
                                        <Typography variant="caption" color={THEME.secondary}>
                                            Target
                                        </Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={target}
                                                onChange={(e) => setTarget(e.target.value)}
                                                sx={{
                                                    color: THEME.textMain,
                                                    bgcolor: THEME.cardBg,
                                                    '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.border },
                                                }}
                                            >
                                                {nodeOptions.map((n) => (
                                                    <MenuItem key={n.id} value={n.id}>
                                                        {n.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} md={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : problem === 'analyze' ? <AnalyticsIcon /> : <RouteIcon />}
                                    onClick={runSolver}
                                    disabled={loading || !graph.nodes?.length}
                                    sx={{
                                        bgcolor: THEME.primary,
                                        color: '#000',
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: THEME.primaryHover },
                                    }}
                                >
                                    {loading ? 'Running…' : 'Run'}
                                </Button>
                            </Grid>
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        <MarkovGraphCanvas
                            onGraphChange={handleGraphChange}
                            problem={problem}
                            result={result}
                            optimalEdgeIds={optimalEdgeIds}
                        />

                        {result && (
                            <Box
                                sx={{
                                    mt: 3,
                                    p: 2,
                                    bgcolor: THEME.cardBg,
                                    border: `1px solid ${THEME.border}`,
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="subtitle1" color={THEME.primary} fontWeight={700} mb={1}>
                                    Results
                                </Typography>
                                {result.stationary && (
                                    <Typography variant="body2" color={THEME.secondary} component="div">
                                        Stationary:{' '}
                                        {Object.entries(result.stationary)
                                            .map(([id, p]) => `${result.labels?.[id] || id}: ${Number(p).toFixed(3)}`)
                                            .join(' · ')}
                                    </Typography>
                                )}
                                {result.path_labels && (
                                    <Typography variant="body2" color={THEME.secondary}>
                                        Path: {result.path_labels.join(' → ')}
                                        {result.cost != null && ` (cost ${Number(result.cost).toFixed(3)})`}
                                        {result.probability != null &&
                                            ` (probability ${Number(result.probability).toFixed(4)})`}
                                    </Typography>
                                )}
                                {result.values && (
                                    <Typography variant="body2" color={THEME.secondary} component="div">
                                        State values:{' '}
                                        {Object.entries(result.values)
                                            .map(([id, v]) => `${id}: ${Number(v).toFixed(3)}`)
                                            .join(' · ')}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>

            <FooterSection />
        </Box>
    );
}

export default GraphEditor;
